import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { Settlement } from '../../../models/Entities/Settlements/Settlement';
import { CreditorInvoice } from '../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import {
    appliedAmountOf,
    INVOICE_STATUS_KEYS,
    invoiceLabel,
    invoiceStatusOf,
    pendingAmountOf
} from '../../../functions/CreditorInvoices.Functions';
import {
    CreditorPayment,
    PractitionerPatientsSummary,
    PractitionerVisitsSummary
} from '../../../models/Entities/PractitionerAccountStatement/PractitionerAccountStatement';
import {
    buildFileName,
    controlBreakRows,
    dateCell,
    ExcelRow,
    ExcelSheet,
    integer,
    money,
    rowKindOf
} from '../../Contracts/statistics/StatisticsExcel.Functions';
import {
    BalanceByCurrency,
    isOtherReceiver,
    paymentSettlementIds,
    SettlementRow,
    settlementLabel,
    subtotalsByCurrency,
    protocolLabel
} from './PractitionerAccountStatement.Functions';

/**
 * El libro del estado de cuenta: Resumen, Por liquidación (con subtotal por
 * moneda), Pagos (con receptor, facturas y comprobantes), Facturas, Deudas
 * pendientes y Actividad. Sin efectos: la descarga la hace downloadWorkbook.
 */

export const SHEET_NAMES = ['Resumen', 'Por liquidación', 'Pagos', 'Facturas', 'Deudas pendientes', 'Actividad'];

/** Estado de la factura en el Excel (mismo texto que la pastilla). */
const INVOICE_STATUS_TEXT: Record<string, string> = {
    unpaid: 'Sin pago',
    partial: 'Pagada parcialmente',
    paid: 'Pagada',
    linked: 'Asociada'
};

/** estado-de-cuenta-{profesional}-{aaaa-mm-dd}.xlsx */
export const accountStatementFileName = (practitionerName: string, date: Date = new Date()): string =>
    buildFileName(`estado de cuenta ${practitionerName || 'profesional'}`, date);

/** Los cortes por moneda nunca se suman entre sí: se saca el total general de controlBreakRows. */
const withoutGrandTotal = (rows: ExcelRow[]): ExcelRow[] => {
    const index = rows.findIndex((row) => rowKindOf(row) === 'total');
    if (index < 0) return rows;
    // También el renglón en blanco que lo separa.
    const start = index > 0 && Object.keys(rows[index - 1]).length === 0 ? index - 1 : index;
    return rows.slice(0, start);
};

export interface AccountStatementExcelInput {
    practitionerName: string;
    practitionerId: string;
    periodLabel: string;
    balance: BalanceByCurrency[];
    settlementRows: SettlementRow[];
    payments: CreditorPayment[];
    pendingDebts: Debt[];
    settlements: Settlement[];
    patients?: PractitionerPatientsSummary;
    visits?: PractitionerVisitsSummary;
    nameOf: (identifier: string) => string;
    statusLabel: (status: number | undefined) => string;
    /** Estado de paciente traducido (el API lo manda en inglés). */
    patientStatusLabel?: (status: number | null, statusName: string) => string;
    /** Facturas del profesional (contrato §12); sin el dato, la hoja queda con una nota. */
    invoices?: CreditorInvoice[];
}

/** Facturas que cubre el pago: los números (de la lista de facturas) o, si no, la cantidad. */
const paymentInvoicesText = (payment: CreditorPayment, invoices: CreditorInvoice[] | undefined): string | ReturnType<typeof integer> => {
    const covered = (invoices ?? []).filter((invoice) => (invoice.payments ?? []).some((item) => item.paymentId === payment.paymentId));
    if (covered.length > 0) return covered.map(invoiceLabel).join(', ');
    return payment.invoiceCount === undefined || payment.invoiceCount === null ? '' : integer(payment.invoiceCount);
};

export const summarySheet = (input: AccountStatementExcelInput): ExcelSheet => ({
    name: 'Resumen',
    description: `Lo que le debemos a ${input.practitionerName}, por moneda. Pagado en el período: ${input.periodLabel}.`,
    note: 'Los importes de monedas distintas no se suman entre sí.',
    rows: input.balance.map((row) => ({
        Moneda: row.currency,
        'Le debemos (pendiente)': money(row.pending),
        'Total liquidado': money(row.original),
        'Ya pagado': money(row.paid),
        'Liquidaciones pendientes de pago': integer(row.pendingSettlements),
        'Deudas pendientes': integer(row.pendingDebts),
        'Pagado en el período': money(row.paidInPeriod),
        'Pagos en el período': integer(row.paymentsInPeriod),
        'Pagado a otro receptor en el período': money(row.paidToOthersInPeriod)
    }))
});

const SETTLEMENT_HEADERS = ['Moneda', 'Liquidación', 'Desde', 'Hasta', 'Estado', 'Deudas', 'Total liquidado', 'Pagado', 'Pendiente', '% pagado'];

export const bySettlementSheet = (input: AccountStatementExcelInput): ExcelSheet => {
    const subtotals = subtotalsByCurrency(input.settlementRows);
    const groups = subtotals.map((total) => ({
        label: total.currency,
        totals: { 'Total liquidado': money(total.original), Pagado: money(total.paid), Pendiente: money(total.pending) },
        rows: input.settlementRows
            .filter((row) => row.currency === total.currency)
            .map((row) => ({
                Liquidación: row.name,
                Desde: dateCell(row.periodFrom),
                Hasta: dateCell(row.periodTo),
                Estado: row.settlementId === null ? '' : input.statusLabel(row.status),
                Deudas: integer(row.debtCount),
                'Total liquidado': money(row.original),
                Pagado: money(row.paid),
                Pendiente: money(row.pending),
                '% pagado': integer(row.progress)
            }))
    }));
    return {
        name: 'Por liquidación',
        description: 'Cada liquidación con lo liquidado, lo pagado y lo pendiente, con el acumulado por moneda.',
        headers: SETTLEMENT_HEADERS,
        autofilter: false,
        rows: withoutGrandTotal(controlBreakRows(groups, 'Moneda', 'Liquidación'))
    };
};

export const paymentsSheet = (input: AccountStatementExcelInput): ExcelSheet => ({
    name: 'Pagos',
    description: 'Pagos imputados a las deudas del profesional y a quién se le pagó.',
    rows: input.payments.map((payment) => {
        const other = isOtherReceiver(payment, input.practitionerId);
        return {
            Fecha: dateCell(payment.paymentDate),
            'N° de pago': integer(payment.paymentId),
            Moneda: (payment.currencyCode || '').toUpperCase(),
            Monto: money(payment.amount),
            'Imputado a la deuda del profesional': money(payment.totalImputedToCreditor),
            Receptor: input.nameOf(payment.receiver),
            'Pagado a otro receptor': other ? 'Sí' : 'No',
            Liquidaciones: paymentSettlementIds(payment).map((id) => settlementLabel(id, input.settlements)).join(', '),
            Facturas: paymentInvoicesText(payment, input.invoices),
            Comprobantes: payment.receiptCount === undefined || payment.receiptCount === null ? '' : integer(payment.receiptCount),
            Comentarios: payment.comments || ''
        };
    })
});

const INVOICE_HEADERS = ['Número', 'Fecha', 'Liquidación', 'Moneda', 'Importe', 'Aplicado', 'Pendiente', 'Estado', 'Pagos que la cubren', 'Origen', 'Archivo', 'Comentarios'];

export const invoicesSheet = (input: AccountStatementExcelInput): ExcelSheet => ({
    name: 'Facturas',
    description: `Facturas de ${input.practitionerName}, qué pagos las cubren y cuánto falta pagar de cada una (en su moneda).`,
    headers: INVOICE_HEADERS,
    note: input.invoices === undefined ? 'Las facturas del profesional todavía no están disponibles.' : undefined,
    rows: (input.invoices ?? []).map((invoice) => {
        const pending = pendingAmountOf(invoice);
        return {
            Número: invoice.invoiceNumber || '',
            Fecha: dateCell(invoice.invoiceDate ?? undefined),
            Liquidación: invoice.settlementId ? settlementLabel(invoice.settlementId, input.settlements) : '',
            Moneda: (invoice.currencyCode || '').toUpperCase(),
            Importe: invoice.amount === null || invoice.amount === undefined ? '' : money(invoice.amount),
            Aplicado: money(appliedAmountOf(invoice)),
            Pendiente: pending === undefined ? '' : money(pending),
            Estado: INVOICE_STATUS_TEXT[INVOICE_STATUS_KEYS[invoiceStatusOf(invoice)]],
            'Pagos que la cubren': (invoice.payments ?? []).map((item) => `#${item.paymentId}`).join(', '),
            Origen: invoice.source === 2 ? 'Factura de liquidación' : 'Subida en el estado de cuenta',
            Archivo: invoiceLabel({ ...invoice, invoiceNumber: null }),
            Comentarios: invoice.comments || ''
        };
    })
});

export const pendingDebtsSheet = (input: AccountStatementExcelInput): ExcelSheet => ({
    name: 'Deudas pendientes',
    description: 'Deudas del centro con el profesional que todavía tienen saldo, más viejas primero.',
    rows: input.pendingDebts.map((debt) => ({
        'N° de deuda': integer(debt.id),
        Fecha: dateCell(debt.debtDate),
        Liquidación: settlementLabel(debt.settlementId, input.settlements),
        Moneda: (debt.currencyCode || '').toUpperCase(),
        Monto: money(debt.originalAmount),
        Pagado: money(debt.paidAmount),
        Pendiente: money(debt.pendingAmount),
        Comentario: debt.comments || ''
    }))
});

export const activitySheet = (input: AccountStatementExcelInput): ExcelSheet => {
    const rows: ExcelRow[] = [];
    if (input.patients) {
        const patientStatus = input.patientStatusLabel ?? ((_status: number | null, name: string) => name);
        rows.push({
            Indicador: 'Pacientes a cargo',
            Detalle: input.patients.source === 'Consumptions' ? 'Total (calculado a partir de sus consumos)' : 'Total',
            Cantidad: integer(input.patients.totalPatients),
            Activos: input.patients.activePatients === undefined ? '' : integer(input.patients.activePatients)
        });
        input.patients.byStatus.forEach((item) => rows.push({ Indicador: 'Pacientes por estado', Detalle: patientStatus(item.status, item.statusName), Cantidad: integer(item.count) }));
        input.patients.byProtocol.forEach((item) =>
            rows.push({ Indicador: 'Pacientes por protocolo', Detalle: protocolLabel(item.protocolNumber, item.protocolName), Cantidad: integer(item.patients), Activos: integer(item.active) })
        );
    }
    if (input.visits) {
        rows.push({ Indicador: 'Visitas en el período', Detalle: input.periodLabel, Cantidad: integer(input.visits.totalVisits) });
        input.visits.byStatus.forEach((item) => rows.push({ Indicador: 'Visitas por estado', Detalle: item.statusName, Cantidad: integer(item.count) }));
        input.visits.byProtocol.forEach((item) => rows.push({ Indicador: 'Visitas por protocolo', Detalle: protocolLabel(item.protocolNumber, item.protocolName), Cantidad: integer(item.count) }));
        input.visits.byMonth.forEach((item) =>
            rows.push({ Indicador: 'Visitas por mes', Detalle: `${String(item.month).padStart(2, '0')}/${item.year}`, Cantidad: integer(item.count) })
        );
    }
    return {
        name: 'Actividad',
        description: 'Pacientes a cargo y visitas del profesional.',
        headers: ['Indicador', 'Detalle', 'Cantidad', 'Activos'],
        note: input.patients && input.visits ? undefined : 'Parte de la actividad todavía no está disponible.',
        rows
    };
};

/** Las seis hojas, en orden. */
export const accountStatementSheets = (input: AccountStatementExcelInput): ExcelSheet[] => [
    summarySheet(input),
    bySettlementSheet(input),
    paymentsSheet(input),
    invoicesSheet(input),
    pendingDebtsSheet(input),
    activitySheet(input)
];
